import React, { useEffect, useRef, useState } from 'react';
import CloseButton from './CloseModalButton';
import InputField from './InputField';
import useClickOutside from '../../hooks/useModalClickHandler';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { updateUserName } from '../../services/UserServices';
import { UserNameUpdate } from 'settings/types/User';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from '../../../localization/useTranslations';

interface EditPersonalInfoProps {
  onClose: () => void;
  isOpen: boolean;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  handleUserInfoChange: (updatedInfo: any) => void;
  userId: string | number;
  setIsSnackbarOpen: (value: boolean) => void;
  setSnackbarMessage: (message: string) => void;
}

const EditPersonalInfo: React.FC<EditPersonalInfoProps> = ({
  onClose,
  firstName,
  lastName,
  avatarUrl,
  handleUserInfoChange,
  userId,
  setIsSnackbarOpen,
  setSnackbarMessage,
}) => {
  const translations = useTranslations();

  const validationSchema = yup
    .object({
      firstName: yup
        .string()
        .required(translations.myAccountSettings?.valueRequired || 'Value is required')
        .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
      lastName: yup
        .string()
        .required(translations.myAccountSettings?.valueRequired || 'Value is required')
        .max(50, translations.salesFunnel.maxLength50ErrorMessage || 'Maximum length is 50 characters'),
    })
    .required();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [image, setImage] = React.useState([]);
  const [personalInfo, setPersonalInfo] = useState<UserNameUpdate>({
    firstName: firstName,
    lastName: lastName,
    userId: Number(userId),
    avatar: '',
    prevAvatarUrl: avatarUrl,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const imageErrors = {
    acceptType:
      translations?.myAccountSettings?.acceptType || 'Unsupported file format. Please select a JPEG or PNG image.',
    maxNumber: translations?.myAccountSettings?.maxNumber || 'You can only upload one image.',
    minSize: translations?.myAccountSettings?.minSize || 'Image size must min 300KB.',
    resolution: translations.myAccountSettings.imageSizeRequirements || 'The image must be at least 300x300 pixels',
    tooLarge: translations.myAccountSettings.fileSizeTooLarge || 'File is too large.',
  };

  const onChange = (img: ImageListType, addUpdateIndex: number[] | undefined) => {
    setImage(img[0].dataURL as any);
    setPersonalInfo((prev) => ({
      ...prev,
      avatar: img[0].file,
    }));
  };

  // Method to handle form submission
  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    // Combine form data with image
    const formData = {
      ...data,
      avatar: personalInfo.avatar,
      userId: Number(userId),
      prevAvatarUrl: avatarUrl,
    };

    try {
      const response = await updateUserName(formData);

      if (response.status === 'ok') {
        handleUserInfoChange({
          ...data,
          avatarUrl: image.length > 0 ? image : avatarUrl,
        });

        setSnackbarMessage(translations.snackBarMessages.userProfileHasBeenUpdated || 'User profile has been updated.');

        setIsSnackbarOpen(true);

        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // handle image changes and update monogram element if image is not present
  useEffect(() => {
    const monogramElement = document.querySelector('#navigation__user-button-li');

    const navbarImageElement = document.querySelector('#menu-user-avatar');

    const sidebarImageElement = document.querySelector('#drawer-menu-user-avatar');

    const drawerUserNameElement = document.querySelector('#drawer-menu-user-name');

    if (monogramElement && avatarUrl) {
      navbarImageElement?.setAttribute('src', avatarUrl);

      sidebarImageElement?.setAttribute('src', avatarUrl);
    } else if (monogramElement && !avatarUrl) {
      // Refresh monogram element
      monogramElement.textContent = `${firstName[0]}${lastName[0]}`;
    }

    if (drawerUserNameElement) {
      drawerUserNameElement.textContent = `${firstName} ${lastName}`;
    }
  }, [avatarUrl, firstName, lastName]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useClickOutside(dialogRef, onClose);

  return (
    <div
      className="settings-modal modal modal--large settings-modal--form settings-modal--personal-info-modal"
      ref={dialogRef}
    >
      <div className="modal__wrapper">
        <div className="settings-modal__header modal__header">
          <h5 className="settings-modal__title">
            {translations.myAccountSettings.editPersonalInformation || 'Edit Personal Information'}
          </h5>
          <CloseButton onClose={onClose} />
        </div>
        <FormProvider {...methods}>
          <form className="settings-modal__form" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="settings-modal__content modal__content">
              <div className="settings-modal__field settings-modal__field--file">
                <img
                  src={typeof image === 'string' ? image : avatarUrl}
                  className="settings-modal__field-image"
                  alt="avatar"
                />

                <div className="settings-modal__field-inner">
                  <ImageUploading
                    value={image}
                    onChange={onChange}
                    maxNumber={1}
                    acceptType={['jpg', 'jpeg', 'png']}
                    // maxFileSize={}
                    resolutionHeight={300}
                    resolutionWidth={300}
                    resolutionType="more"
                  >
                    {({
                      imageList,
                      onImageUpload,
                      onImageRemoveAll,
                      onImageUpdate,
                      onImageRemove,
                      isDragging,
                      dragProps,
                      errors,
                    }) => (
                      <div className="upload__image-wrapper">
                        <button
                          className={`settings-modal__label link-underline-16 ${isSubmitting ? 'disabled' : ''}`}
                          style={isDragging ? { color: 'red' } : undefined}
                          onClick={onImageUpload}
                          {...dragProps}
                          type="button"
                        >
                          {translations.myAccountSettings.uploadNewImage || 'Upload New Image'}
                        </button>
                        &nbsp;
                        {errors && (
                          <div className="settings-modal__field-error input-error">
                            {errors.maxNumber && <span>{imageErrors.maxNumber}</span>}
                            {errors.acceptType && <span>{imageErrors.acceptType}</span>}
                            {errors.maxFileSize && <span>{imageErrors.minSize}</span>}
                            {errors.resolution && <span>{imageErrors.resolution}</span>}
                          </div>
                        )}
                      </div>
                    )}
                  </ImageUploading>
                  <span className="settings-modal__field-description basic basic-14">
                    {translations.myAccountSettings.imageRequirements ||
                      'The image should be square, min. 300x300 px, min. size 300 KB'}
                  </span>
                </div>
              </div>

              <div className="settings-modal__field settings-modal__field--half">
                <InputField
                  name="firstName"
                  label={translations.myAccountSettings.firstName || 'First Name'}
                  type="text"
                  currentValue={firstName}
                  disabled={isSubmitting}
                />
              </div>

              <div className="settings-modal__field settings-modal__field--half">
                <InputField
                  name="lastName"
                  label={translations.myAccountSettings.lastName || 'Last Name'}
                  type="text"
                  currentValue={lastName}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="settings-modal__footer modal__footer">
              <button type="submit" className="settings-modal__button link-16" disabled={isSubmitting}>
                {translations?.myAccountSettings?.saveChanges || 'Save Changes'}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default EditPersonalInfo;
